/**
 * CustomHeaderNotificacoes.tsx — Sino + gaveta (offcanvas) de notificações.
 *
 * Markup dos cards copiado do tema (theme/adminux, offcanvas #view-notification):
 * `card adminuiux-card` com h6 título + texto + linha (tempo / lixeira theme-red);
 * `alert` colorido para os que pedem ação (destaque). Card inteiro clicável; a
 * lixeira usa stopPropagation. Renderizado no body via portal (fora do navbar).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { sincronizarBadge } from '@/lib/badge';
import { tempoRelativo } from '@/lib/data';
import { updateCoverImages } from '@/utils/layout';
import { IconBell } from './icons';

interface Notif {
  id: number;
  tipo: string;
  texto: string;
  link?: string | null;
  os_id?: number | null;
  lida: boolean;
  origem_nome?: string | null;
  origem_foto?: string | null;
  criado_em?: string | null;
  // Vindos do catálogo, no backend — ver o comentário abaixo.
  titulo?: string;
  icone?: string;
  cor?: string;
  destaque?: boolean;
  // `decisao` não se arquiva enquanto aberta: alguém está esperando do outro
  // lado, e a lixeira aqui seria varrer isso para debaixo do tapete.
  tipo_aviso?: 'recado' | 'pendencia' | 'decisao';
  estado?: string;
}

// ─── A APARÊNCIA VEM DO BACKEND ──────────────────────────────────────────────
//
// Até 29/08/2026 havia aqui um `ESTILO` hardcoded — um mapa de tipo para
// título, ícone e cor. Registrar um aviso novo exigia mexer neste arquivo, e
// quem plugasse um evento no backend via um sino cinza genérico sem entender
// por quê: o contrato estava do lado errado da linha.
//
// Agora `titulo`, `icone`, `cor` e `destaque` chegam prontos de
// `/api/avisos`, montados pelo catálogo (módulo decide o ícone, severidade
// decide a cor). Este arquivo voltou a ser só apresentação.
const PADRAO = { titulo: 'Aviso', icone: 'bi-bell', cor: 'secondary', destaque: false };

export default function CustomHeaderNotificacoes() {
  const navigate = useNavigate();
  const [itens, setItens] = useState<Notif[]>([]);
  const [naoLidas, setNaoLidas] = useState(0);

  async function carregar() {
    try {
      const r = await api.get<{ nao_lidas: number; itens: Notif[] }>(
        '/avisos',
      );
      setItens(r.itens ?? []);
      const quantas = r.nao_lidas ?? 0;
      setNaoLidas(quantas);
      // O ÍCONE CONTA A MESMA COISA QUE O SININHO. O service worker PÕE o badge
      // quando o push chega, mas nada o TIRARIA quando a pessoa abre o app: o
      // vermelho sumiria da tela e o número ficaria no ícone para sempre. Badge
      // que não zera ensina a ignorar o badge — e, no celular, "ignorar"
      // costuma virar "desligar as notificações no sistema".
      sincronizarBadge(quantas);
    } catch {
      /* silencioso */
    }
  }

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 60000);
    return () => clearInterval(t);
  }, []);

  // ⚠️ As fotos NÃO usam `coverimg`: ela esconde o `<img>` e depende de um JS
  // do tema que só roda no carregamento da página — a gaveta é montada depois,
  // então o avatar ficaria vazio. Ver `estilos/_avatar.scss`.
  // Reprocessa sempre que a lista muda, senão as fotos só aparecem ao reabrir.
  useEffect(() => {
    updateCoverImages();
  }, [itens]);

  async function abrir(n: Notif) {
    if (!n.lida) {
      try {
        await api.post(`/avisos/${n.id}/lido`);
      } catch {
        /* ignora */
      }
    }
    if (n.link) navigate(n.link);
    carregar();
  }

  // ABRIR A GAVETA É O QUE ZERA O BADGE — e não marca nada como lido.
  //
  // VISTA ≠ LIDA: a pessoa viu que havia avisos, não leu cada um. A marca de
  // não-lida continua na linha até ela abrir aquele aviso. Sem essa separação,
  // ou o número nunca zera, ou some da lista o que ninguém abriu.
  //
  // A DECISÃO É A EXCEÇÃO e continua contando depois de vista: é o único caso
  // em que há outra pessoa parada esperando.
  async function marcarVistas() {
    try {
      await api.post('/avisos/vistas');
      setNaoLidas((n) => n); // o número certo chega no `carregar()` abaixo
    } catch {
      /* ignora */
    }
    carregar();
  }

  async function marcarTodas() {
    try {
      await api.post('/avisos/lidos');
    } catch {
      /* ignora */
    }
    carregar();
  }

  async function apagar(n: Notif) {
    try {
      await api.delete(`/avisos/${n.id}`);
    } catch {
      /* ignora */
    }
    carregar();
  }

  // Conteúdo interno do card (igual ao markup do tema).
  function corpo(n: Notif, est: typeof PADRAO) {
    return (
      <div className="row gx-3">
        <div className="col-auto">
          {n.origem_foto && !est.destaque ? (
            <figure className="avatar avatar-30 casaa-foto-no-avatar rounded-circle mb-0">
              <img src={n.origem_foto} alt="" />
            </figure>
          ) : (
            <figure
              className={`avatar avatar-30 rounded-circle mb-0 ${
                est.destaque
                  ? `bg-${est.cor} text-white`
                  : `bg-${est.cor}-subtle text-${est.cor}`
              }`}
            >
              <i className={`bi ${est.icone}`}></i>
            </figure>
          )}
        </div>
        <div className="col">
          <h6 className="mb-1">{est.titulo}</h6>
          <p className="small mb-2">{n.texto}</p>
          <div className="row gx-3 align-items-center">
            <div className="col">
              <p className="text-secondary small mb-0">{tempoRelativo(n.criado_em)}</p>
            </div>
            {/* Decisão aberta NÃO se arquiva: alguém está esperando do outro
                lado, e a lixeira aqui seria varrer isso para debaixo do tapete.
                O backend recusa de qualquer forma — esconder o botão é para a
                pessoa não tentar e achar que o app quebrou. */}
            {!(n.tipo_aviso === 'decisao' && n.estado !== 'resolvida') && (
              <div className="col-auto">
                <button
                  type="button"
                  className="btn btn-sm btn-square btn-link theme-red"
                  title="Arquivar"
                  onClick={(e) => {
                    e.stopPropagation();
                    apagar(n);
                  }}
                >
                  <i className="bi bi-archive"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Sino — markup fiel ao template AdminUX (adminux-finance-dashboard):
          btn-square + btn-icon, badge position-absolute top-0 end-0 rounded-pill
          bg-danger p-1 com <small>. */}
      <button
        className="btn btn-link btn-square btn-icon btn-link-header position-relative no-caret"
        type="button"
        data-bs-toggle="offcanvas"
        data-bs-target="#view-notification"
        aria-expanded="false"
        title="Avisos"
        onClick={marcarVistas}
      >
        <IconBell />
        {naoLidas > 0 && (
          <span className="position-absolute top-0 end-0 badge rounded-pill bg-danger p-1">
            <small>{naoLidas > 9 ? '9+' : naoLidas}</small>
            <span className="visually-hidden">não lidas</span>
          </span>
        )}
      </button>

      {/* Gaveta — no body (fora do navbar) p/ funcionar como offcanvas */}
      {createPortal(
        <div
          className="offcanvas offcanvas-end maxwidth-300"
          tabIndex={-1}
          id="view-notification"
          data-bs-scroll="true"
          data-bs-backdrop="false"
        >
          <div className="offcanvas-header border-bottom">
            <div className="flex-grow-1">
              <h6 className="mb-0">Notificações</h6>
              <p className="text-secondary small mb-0">
                {naoLidas > 0 ? `${naoLidas} nova(s)` : 'tudo em dia'}
              </p>
            </div>
            {naoLidas > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-link me-1"
                onClick={marcarTodas}
              >
                marcar lidas
              </button>
            )}
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Fechar"
            ></button>
          </div>

          <div className="offcanvas-body">
            {itens.length === 0 ? (
              <p className="text-secondary small text-center py-4 mb-0">
                Nenhum aviso.
              </p>
            ) : (
              itens.map((n) => {
                const est = { ...PADRAO, ...n };
                return est.destaque ? (
                  <div
                    key={n.id}
                    className={`alert alert-${est.cor} mb-2`}
                    role="button"
                    data-bs-dismiss="offcanvas"
                    onClick={() => abrir(n)}
                    style={{ cursor: 'pointer' }}
                  >
                    {corpo(n, est)}
                  </div>
                ) : (
                  <div
                    key={n.id}
                    className="card adminuiux-card mb-2"
                    role="button"
                    data-bs-dismiss="offcanvas"
                    onClick={() => abrir(n)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-body">{corpo(n, est)}</div>
                  </div>
                );
              })
            )}
          </div>

          {/* RODAPÉ — o caminho para a Inbox.
              A gaveta muda de papel: passa a ser "o que chegou agora", e deixa
              de ser o lugar onde se administra aviso. Teto de 10 itens e daqui
              se vai para a tela inteira.
              ⚠️ SEM DESTINO ainda: `/inbox` nasce na fase 3. Botão desabilitado
              é melhor que link que leva a 404 — e a frase diz o porquê. */}
          <div className="offcanvas-footer border-top p-3">
            <button
              type="button"
              className="btn btn-sm btn-link w-100 text-secondary"
              disabled
              title="A Inbox chega na próxima fatia"
            >
              Ver tudo na Inbox
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
